import React,{ useState, useEffect, useCallback, ReactFragment, ReactPortal, ReactChild } from 'react'
import PageContainer from '../../../CSIComponents/PageContainer';
import { Row, Col, Card, Button, Form, Select } from 'antd';
import Input from '../../../ui/Input';
import CSIActionsButtonsComponent from '../../../Globals/CsiActionButtonsComponent';
import Loader from '../../../Loading'
import { propTypesCarboon, carboonFootprintType, bodyCarboonFootprintType, carboonFootprintRow } from '../../../Categories/Communications/types'
import { validateFormHelper, postHelper, getHelper } from '../../../Categories/Communications/helpers'

const CarbFootPrint = (props: propTypesCarboon) => {
  // setting up the static values
  const FormItem = Form.Item
  const [form] = Form.useForm()
  const Option = Select.Option
  const [disableFlag, setDisableFlag] = useState<boolean>(false)
  const [isonEdit, setIsOnEdit] = useState<boolean>(false)
  const options: string[] = ['Kilogram(s)', 'Ounce(s)', 'Pound(s)']
  const [ready, setReady] = useState<boolean>(false)
  // persiting the updated Values
  const [carboon, setCarboon] = useState<carboonFootprintRow | null>()
  const [id, setId] = useState<string | null>()
  const [langid, setLangid] = useState<string | null>()
  const [perMeeting, setPerMeeting] = useState<string | null>()
  const [perRoomNight, setPerRoomNight] = useState<string | null>()
  const [weatherNormalized, setWeatherNormalized] = useState<string | null>()
  const [permeetingeventcarbonfactortime, setPerMeetingEventCarbonFactorTime] = useState<string | null>()
  const [perroomnightcarbonfactortime, setPeRoomNightCarbonFactortime] = useState<string | null>()
  const [weathernormalizedcarbonfootprinttimeperiod, setWeatherNormalizedCarbonFootprintTimePeriod] = useState<string | null>()
  const [unitOfMeasure, setUnitOfMeasure] = useState<string | null>()

  const Loading: () =>  boolean | ReactChild | ReactFragment | ReactPortal | null = () => {
    return (
      <Loader type="medium" />
    )
  }

  // validating the required values have been fufilled properly
  const validateForm = useCallback(() => {
    /* istanbul ignore next */
    validateFormHelper(form, setDisableFlag)
  },[form])

  const getCarboon = useCallback(async () => {
    /* istanbul ignore next */
    try {
      const response: carboonFootprintType = await getHelper('/property/environmentalimpact')
      if (response?.property?.environmentalimpact) {
        setId(response?.property?.environmentalimpact?.id)
        setLangid(response?.property?.environmentalimpact?.langid)
        setCarboon(response?.property?.environmentalimpact?.carbonfootprint)
      }
      // assign it values in case it is a PUT service
      if (response?.property?.environmentalimpact?.carbonfootprint) {
        setPerMeeting(response?.property?.environmentalimpact?.carbonfootprint.permeetingevent)
        setPerRoomNight(response?.property?.environmentalimpact?.carbonfootprint.perroomnight)
        setWeatherNormalized(response?.property?.environmentalimpact?.carbonfootprint.weathernormalized)
        setPerMeetingEventCarbonFactorTime(response?.property?.environmentalimpact?.carbonfootprint.permeetingeventcarbonfactortime)
        setPeRoomNightCarbonFactortime(response?.property?.environmentalimpact?.carbonfootprint.perroomnightcarbonfactortime)
        setWeatherNormalizedCarbonFootprintTimePeriod(response?.property?.environmentalimpact?.carbonfootprint.weathernormalizedcarbonfootprinttimeperiod)
        setUnitOfMeasure(response?.property?.environmentalimpact?.carbonfootprint.uom)
      }
    } catch (error) {
      /* istanbul ignore next */
      return error
    }
  },[])

  // post call
  const post = async() => {
    /* istanbul ignore next */
    try {
      const body: bodyCarboonFootprintType = {
        supplier: '',
        propertyId: '',
        property: {
          environmentalimpact: {
            id,
            langid,
            carbonfootprint: {
              permeetingevent: perMeeting, permeetingeventcarbonfactortime,
              perroomnight: perRoomNight,
              perroomnightcarbonfactortime,
              uom: unitOfMeasure,
              weathernormalized: weatherNormalized,
              weathernormalizedcarbonfootprinttimeperiod
            }
          }
        }
      }
      const response: carboonFootprintType = await postHelper('/property/env/carbonfootprint', body)
      // persisting the post data
      if (response?.property?.environmentalimpact?.carbonfootprint) {
        setCarboon(response?.property?.environmentalimpact?.carbonfootprint)
      }
      setIsOnEdit(false)
      props.cancelEditable()
      props.disableTabPanes(false)
    } catch (error) {
      /* istanbul ignore next */
      return error
    }
  }

  const persistingData = () => {
    /* istanbul ignore next */
    return post()
  }

  const updateCancel = async () => {
    form.resetFields()
    setIsOnEdit(false)
    props.cancelEditable()
    props.disableTabPanes(false)
  }

  // mount
  useEffect(() => {
    const init = async() => {
      await getCarboon()
      return setReady(true)
    }
    init()
  },[getCarboon, isonEdit])

  return (
    <>
    {ready ?
      <PageContainer>
          <Card
            style={{ marginTop: -15, marginBottom: 60 }}
            title="Carbon Footprint"
            headStyle={{ fontWeight: 600, fontSize: 14, color: '#333', paddingTop: '8px', paddingLeft: '4px' }}
            extra={
              <Button
                type="primary"
                className={!isonEdit ? "db-edit-btn" : "d-none"}
                onClick={() => { 
                  setIsOnEdit(true);
                  validateForm()
                  props.isEditable('1')
                  props.disableTabPanes(true)
                }}>
                Edit
              </Button>
            }
          >
            <Form
                form={form}
                layout="vertical"
                className={!isonEdit ? 'is-disabled-fields' : ''} 
                onValuesChange={validateForm}
                initialValues={{
                  permeetingevent: carboon?.permeetingevent || '',
                  perroomnight: carboon?.perroomnight || '',
                  weatherNormalized: carboon?.weathernormalized || '',
                  permeetingeventcarbonfactortime: carboon?.permeetingeventcarbonfactortime || '',
                  perroomnightcarbonfactortime: carboon?.perroomnightcarbonfactortime || '',
                  weathernormalizedcarbonfootprinttimeperiod: carboon?.weathernormalizedcarbonfootprinttimeperiod || '',
                  unitofmeasure: carboon?.uom || ''
                }}
            >
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item
                    label="Per Meeting/Event"
                    name="permeetingevent"
                    rules={[
                      { pattern: /^[1-9]\d*$/, message: 'This field should be a number' }]}
                  >
                    <Input
                      onChange={(e: { target: { value: string }}) => setPerMeeting(e.target.value)}
                    />
                  </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Per Room Night"
                      name="perroomnight"
                      rules={[
                        {pattern: /^[1-9]\d*$/, message: 'This field should be a number' },{ required: true, message: 'This field is required' }]}
                    >
                      <Input
                        onChange={(e: { target: { value: string }}) => setPerRoomNight(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Weather Normalized"
                      name="weatherNormalized"
                      rules={[
                        { pattern: /^[1-9]\d*$/, message: 'This field should be a number' }]}
                    >
                      <Input
                        onChange={(e: { target: { value: string }}) => setWeatherNormalized(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
              </Row>
              <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item
                      label="Per Meeting/ Event Carbon Factor Time Period"
                      name="permeetingeventcarbonfactortime"
                      extra={isonEdit ? <span className="blue-font">In Months</span> : null}
                      rules={[
                        { pattern: /^[1-9]\d*$/, message: 'This field should be a number' }]}
                    >
                      <Input
                        onChange={(e: { target: { value: string }}) => setPerMeetingEventCarbonFactorTime(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Per Room Night Carbon Factor Time Period"
                      name="perroomnightcarbonfactortime"
                      extra={isonEdit ? <span className="blue-font">In Months</span> : null}
                      rules={[
                        { pattern: /^[1-9]\d*$/, message: 'This field should be a number' }]}
                    >
                      <Input
                        onChange={(e: { target: { value: string }}) => setPeRoomNightCarbonFactortime(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Weather Normalized Carbon Footprint Time Period"
                      name="weathernormalizedcarbonfootprinttimeperiod"
                      extra={isonEdit ? <span className="blue-font">In Months</span> : null}
                      rules={[
                        { pattern: /^[1-9]\d*$/, message: 'This field should be a number' }]}
                    >
                      <Input
                        onChange={(e: { target: { value: string }}) => setWeatherNormalizedCarbonFootprintTimePeriod(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <FormItem 
                    name="unitofmeasure" 
                    label="Unit of Measure"
                    extra={isonEdit ? <span className="blue-font">If user need to push this info to Lanyon, please choose Kilogram(s) or Pound(s).</span> : null}
                    rules={[{ required: true, message: 'This field is required' }]}
                  >
                      <Select allowClear showSearch
                        onChange={(e: string) => setUnitOfMeasure(e)}>
                        {
                          options.map(((opt: string, index:number) => <Option value={opt}
                          key={`checkinkiosk-${index}`}
                          >{opt}</Option>
                          ))
                        }
                      </Select>
                  </FormItem>
                </Col>
              </Row>
            </Form>
          </Card>
      </PageContainer>
      : 
      Loading()
      }
      {
        isonEdit ?
        <CSIActionsButtonsComponent
          setIsOnEdit={updateCancel}
          disableFlag={disableFlag}
          update={persistingData}
        />
        : <></>
      }
  </>
  )
}

export default CarbFootPrint

